/**
 * The body of one PUT: the dirty keys with their current values, cut to what the server will
 * accept. A value over the per-value limit can never be sent and is reported; when the whole body
 * is over the request limit the largest value waits for the next push.
 */

import { MAX_BODY_BYTES, MAX_VALUE_BYTES, utf8Bytes } from './transport';
import type { CloudKey } from './types';

export interface PushBody {
  /** Null when nothing is left to send. */
  body: string | null;
  bytes: number;
  /** Exactly what was put in the body, to compare with the store when the answer comes back. */
  sent: Map<CloudKey, string>;
  /** Keys whose value the server would refuse outright. */
  oversize: CloudKey[];
}

export function buildPushBody(
  baseRevision: number, keys: readonly CloudKey[], read: (key: CloudKey) => string | null,
): PushBody {
  const sent = new Map<CloudKey, string>();
  const sizes = new Map<CloudKey, number>();
  const oversize: CloudKey[] = [];
  for (const key of keys) {
    // A key with no value here goes up as '' - which every reader of these keys takes for "none" -
    // and not as a deletion: '' reaches the other devices and clears their copy, a missing key would not.
    const value = read(key) ?? '';
    const bytes = utf8Bytes(value);
    if (bytes > MAX_VALUE_BYTES) oversize.push(key);
    else {
      sent.set(key, value);
      sizes.set(key, bytes);
    }
  }
  for (;;) {
    if (sent.size === 0) return { body: null, bytes: 0, sent, oversize };
    const body = JSON.stringify({ baseRevision, keys: Object.fromEntries(sent) });
    // Measured after JSON escaping: a save is JSON inside JSON, and every quote in it doubles.
    const bytes = utf8Bytes(body);
    if (bytes <= MAX_BODY_BYTES) return { body, bytes, sent, oversize };
    const largest = [...sent.keys()].reduce((a, b) => ((sizes.get(a) ?? 0) >= (sizes.get(b) ?? 0) ? a : b));
    sent.delete(largest);
    // Alone and still too big once escaped: it will never fit, so it is not retried for ever.
    if (sent.size === 0) oversize.push(largest);
  }
}
